import axios from 'axios';

import various from '../../../General/various';
import {
  clearYowanStoredSession,
  readYowanStoredSnapshot,
  storeYowanAuthSummary,
  storeYowanTokens,
  updateYowanStoredTokens,
} from './yowanAuthStorage';
import {
  clearYowanAuthRuntimeState,
  readYowanAuthRuntimeState,
  writeYowanAuthRuntimeState,
} from './yowanAuthRuntime';

const buildYowanUrl = (path) => `${various.currentDomain}api/yowan/${path}`;

const YOWAN_REFRESH_PATH = 'auth/refresh/';

const buildAuthHeaders = (accessToken) => ({
  'Content-Type': 'application/json; charset=utf-8',
  Authorization: `Bearer ${accessToken}`,
});

const buildJsonHeaders = () => ({
  'Content-Type': 'application/json; charset=utf-8',
});

const collectErrorText = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(collectErrorText).join(' ');
  }

  if (typeof value === 'object') {
    return Object.values(value).map(collectErrorText).join(' ');
  }

  return String(value);
};

export const isYowanInvalidTokenError = (error) => {
  const response = error?.response;

  if (response?.status !== 401) {
    return false;
  }

  const data = response.data || {};
  const text = collectErrorText(data).toLowerCase();
  const hasTokenNotValidSignal =
    data.code === 'token_not_valid' ||
    text.includes('token_not_valid') ||
    text.includes('given token not valid') ||
    text.includes('token is invalid');

  return hasTokenNotValidSignal;
};

export const isYowanExpiredAccessTokenError = (error) => {
  if (!isYowanInvalidTokenError(error)) {
    return false;
  }

  const data = error?.response?.data || {};
  const text = collectErrorText(data).toLowerCase();
  const hasAccessTokenSignal = text.includes('accesstoken') || text.includes('access token');
  const hasExpiredSignal = text.includes('expired');

  return hasAccessTokenSignal && hasExpiredSignal;
};

export const isYowanInvalidAccessTokenError = (error) => {
  if (!isYowanInvalidTokenError(error)) {
    return false;
  }

  const data = error?.response?.data || {};
  const text = collectErrorText(data).toLowerCase();
  const hasRefreshTokenSignal = text.includes('refreshtoken') || text.includes('refresh token');

  return !hasRefreshTokenSignal;
};

let yowanRefreshPromise = null;

export const refreshYowanAccessToken = async () => {
  if (yowanRefreshPromise) {
    return yowanRefreshPromise;
  }

  yowanRefreshPromise = (async () => {
    const snapshot = readYowanStoredSnapshot();

    if (!snapshot.refreshToken) {
      throw new Error('Yowan refresh token is missing.');
    }

    const response = await axios.post(
      buildYowanUrl(YOWAN_REFRESH_PATH),
      {
        refresh: snapshot.refreshToken,
      },
      {
        headers: buildJsonHeaders(),
      }
    );
    const payload = response.data || {};

    if (!payload.access) {
      throw new Error('Yowan refresh response did not include an access token.');
    }

    updateYowanStoredTokens({
      access: payload.access,
      refresh: payload.refresh,
      authDomain: payload.authDomain || payload.auth_domain || snapshot.authDomain,
    });

    return payload;
  })();

  try {
    return await yowanRefreshPromise;
  } finally {
    yowanRefreshPromise = null;
  }
};

export const requestYowanWithAuthRetry = async (requester, { accessToken } = {}) => {
  try {
    return await requester(accessToken || readYowanStoredSnapshot().accessToken);
  } catch (error) {
    if (!isYowanInvalidAccessTokenError(error)) {
      throw error;
    }

    let refreshPayload;

    try {
      refreshPayload = await refreshYowanAccessToken();
    } catch (refreshError) {
      clearYowanStoredSession();
      clearYowanAuthRuntimeState();
      throw refreshError;
    }

    return requester(refreshPayload.access || readYowanStoredSnapshot().accessToken);
  }
};

export const loginYowan = async ({ email, password }) => {
  const response = await axios.post(buildYowanUrl('auth/login/'), {
    email,
    password,
  });

  storeYowanTokens(response.data);
  applyYowanAuthSummary(response.data?.auth_summary);
  return response.data;
};

export const fetchYowanAuthMe = async (accessToken = readYowanStoredSnapshot().accessToken) => {
  const response = await requestYowanWithAuthRetry(
    (retryAccessToken) =>
      axios.get(buildYowanUrl('auth/me/'), {
        headers: buildAuthHeaders(retryAccessToken || accessToken),
      }),
    { accessToken }
  );

  writeYowanAuthRuntimeState({ authMe: response.data });
  return response.data;
};

const normalizeYowanAccessMeStakeholderAccess = (item = null) => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  return {
    ...item,
    authSubjectId: item.authSubjectId || item.auth_subject_id || item.auth_subject?.id || '',
    loginEmail: item.loginEmail || item.login_email || item.auth_subject_email || item.authSubjectEmail || item.auth_subject?.email || '',
    isActive: item.isActive ?? item.is_active,
  };
};

const normalizeYowanAccessMeBlogAuthorSummary = (item = null) => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  return {
    ...item,
    id: item.id || '',
    displayName: item.displayName || item.display_name || item.name || '',
    profileUrl: item.profileUrl || item.profile_url || '',
    isActive: item.isActive ?? item.is_active,
  };
};

const normalizeYowanAccessMeBlogAuthorAccess = (item = null) => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  return {
    ...item,
    id: item.id || '',
    authSubjectId: item.authSubjectId || item.auth_subject_id || item.auth_subject?.id || '',
    loginEmail: item.loginEmail || item.login_email || item.auth_subject_email || item.authSubjectEmail || item.email || item.auth_subject?.email || '',
    canPublishBlog: item.canPublishBlog ?? item.can_publish_blog ?? false,
    isActive: item.isActive ?? item.is_active,
    author: normalizeYowanAccessMeBlogAuthorSummary(item.author || item.blog_author),
  };
};

const normalizeYowanBlogContentPermissions = (item = {}) => ({
  ...item,
  canCreate: item.canCreate ?? item.can_create ?? false,
  canEditOwn: item.canEditOwn ?? item.can_edit_own ?? false,
  canPublishOwn: item.canPublishOwn ?? item.can_publish_own ?? false,
  canDeleteOwn: item.canDeleteOwn ?? item.can_delete_own ?? false,
  canManageAll: item.canManageAll ?? item.can_manage_all ?? false,
});

const normalizeYowanAccessMe = (payload = {}) => ({
  ...payload,
  isInstructor: payload.isInstructor ?? payload.is_instructor,
  isOrganizer: payload.isOrganizer ?? payload.is_organizer,
  canAccessStakeholder: payload.canAccessStakeholder ?? payload.can_access_stakeholder,
  canAccessRelatedSeminars: payload.canAccessRelatedSeminars ?? payload.can_access_related_seminars,
  canAccessRelatedClasses: payload.canAccessRelatedClasses ?? payload.can_access_related_classes,
  instructorAccess: normalizeYowanAccessMeStakeholderAccess(payload.instructorAccess || payload.instructor_access),
  organizerAccess: normalizeYowanAccessMeStakeholderAccess(payload.organizerAccess || payload.organizer_access),
  contentPermissions: payload.contentPermissions || payload.content_permissions || {},
  blogContentPermissions: normalizeYowanBlogContentPermissions(
    payload.blogContentPermissions ||
      payload.blog_content_permissions ||
      payload.contentPermissions?.blog ||
      payload.content_permissions?.blog ||
      {},
  ),
  blogAuthorAccess: normalizeYowanAccessMeBlogAuthorAccess(payload.blogAuthorAccess || payload.blog_author_access),
});

export const fetchYowanAccessMe = async (accessToken = readYowanStoredSnapshot().accessToken) => {
  const response = await requestYowanWithAuthRetry(
    (retryAccessToken) =>
      axios.get(buildYowanUrl('access/me/'), {
        headers: buildAuthHeaders(retryAccessToken || accessToken),
      }),
    { accessToken }
  );
  const payload = normalizeYowanAccessMe(response.data || {});

  writeYowanAuthRuntimeState({ accessMe: payload });
  return payload;
};

export const fetchYowanAdminStatus = async (accessToken = readYowanStoredSnapshot().accessToken) => {
  const response = await requestYowanWithAuthRetry(
    (retryAccessToken) =>
      axios.get(buildYowanUrl('access/admin-status/'), {
        headers: buildAuthHeaders(retryAccessToken || accessToken),
      }),
    { accessToken }
  );

  writeYowanAuthRuntimeState({ adminStatus: response.data });
  return response.data;
};

export const applyYowanAuthSummary = (authSummary = null) => {
  if (!authSummary || typeof authSummary !== 'object') {
    return {
      applied: false,
      authMe: null,
      accessMe: null,
      adminStatus: null,
    };
  }

  const yowanPayload =
    authSummary.domain_payload?.yowan ||
    authSummary.domainPayload?.yowan ||
    authSummary.yowan ||
    {};
  const subject = authSummary.subject || {};
  const commonFlags = authSummary.common_flags || authSummary.commonFlags || {};
  const authMe = yowanPayload.auth_me ||
    yowanPayload.authMe ||
    authSummary.auth_me ||
    authSummary.authMe ||
    {
      id: subject.id || subject.uuid || '',
      email: subject.email || '',
      display_name: subject.display_name || subject.displayName || '',
    };
  const accessMe = normalizeYowanAccessMe(
    yowanPayload.access_me ||
      yowanPayload.accessMe ||
      authSummary.access_me ||
      authSummary.accessMe ||
      {}
  );
  const adminStatus =
    yowanPayload.admin_status ||
    yowanPayload.adminStatus ||
    authSummary.admin_status ||
    authSummary.adminStatus ||
    {
      can_access_admin: commonFlags.can_access_admin ?? commonFlags.canAccessAdmin ?? false,
    };

  storeYowanAuthSummary(authSummary);
  writeYowanAuthRuntimeState({
    authSummary,
    authMe,
    accessMe,
    adminStatus,
  });

  return {
    applied: true,
    authMe,
    accessMe,
    adminStatus,
  };
};

export const fetchYowanAuthSummary = async (accessToken = readYowanStoredSnapshot().accessToken) => {
  const response = await requestYowanWithAuthRetry(
    (retryAccessToken) =>
      axios.get(buildYowanUrl('auth/summary/'), {
        headers: buildAuthHeaders(retryAccessToken || accessToken),
      }),
    { accessToken }
  );
  const authSummary = response.data?.auth_summary || response.data;

  return applyYowanAuthSummary(authSummary);
};

export const syncYowanCurrentState = async () => {
  const snapshot = readYowanStoredSnapshot();

  if (!snapshot.accessToken) {
    return {
      authMe: null,
      accessMe: null,
      adminStatus: null,
    };
  }

  let authMe;
  let accessMe;

  try {
    const summaryResult = await fetchYowanAuthSummary(snapshot.accessToken);
    if (summaryResult.applied) {
      return {
        authMe: summaryResult.authMe,
        accessMe: summaryResult.accessMe,
        adminStatus: summaryResult.adminStatus,
      };
    }
  } catch (error) {
    if (isYowanInvalidTokenError(error)) {
      clearYowanStoredSession();
      clearYowanAuthRuntimeState();
      return {
        authMe: null,
        accessMe: null,
        adminStatus: null,
      };
    }
  }

  try {
    authMe = await fetchYowanAuthMe();
    accessMe = await fetchYowanAccessMe();
  } catch (error) {
    if (!isYowanInvalidTokenError(error)) {
      throw error;
    }

    clearYowanStoredSession();
    clearYowanAuthRuntimeState();
    return {
      authMe: null,
      accessMe: null,
      adminStatus: null,
    };
  }

  const adminStatus = accessMe?.can_access_admin
    ? await fetchYowanAdminStatus()
    : (writeYowanAuthRuntimeState({ adminStatus: { can_access_admin: false } }), { can_access_admin: false });

  return {
    authMe,
    accessMe,
    adminStatus,
  };
};

export const requestYowanPasswordReset = async ({ email }) => {
  const response = await axios.post(buildYowanUrl('auth/password-reset/'), {
    email,
  });

  return response.data;
};

export const confirmYowanPasswordReset = async ({ token, newPassword }) => {
  const response = await axios.post(buildYowanUrl('auth/password-reset-confirm/'), {
    token,
    new_password: newPassword,
  });

  return response.data;
};

export const confirmYowanPasswordSetup = async ({ token, newPassword }) => {
  const response = await axios.post(buildYowanUrl('auth/password-setup-confirm/'), {
    token,
    new_password: newPassword,
  });

  return response.data;
};

export const changeYowanLoginEmail = async ({ currentPassword, newEmail }) => {
  const response = await requestYowanWithAuthRetry((accessToken) =>
    axios.post(
      buildYowanUrl('auth/change-email/'),
      {
        current_password: currentPassword,
        new_email: newEmail,
      },
      {
        headers: buildAuthHeaders(accessToken),
      }
    )
  );
  const payload = response.data || {};
  const currentRuntimeState = readYowanAuthRuntimeState();

  if (payload.email) {
    writeYowanAuthRuntimeState({
      authMe: {
        ...(currentRuntimeState.authMe || {}),
        email: payload.email,
      },
    });
  }

  return payload;
};

export const changeYowanPassword = async ({ currentPassword, newPassword }) => {
  const response = await requestYowanWithAuthRetry((accessToken) =>
    axios.post(
      buildYowanUrl('auth/change-password/'),
      {
        current_password: currentPassword,
        new_password: newPassword,
      },
      {
        headers: buildAuthHeaders(accessToken),
      }
    )
  );

  clearYowanStoredSession();
  clearYowanAuthRuntimeState();

  return response.data;
};

export const logoutYowan = async () => {
  const snapshot = readYowanStoredSnapshot();

  try {
    if (snapshot.accessToken && snapshot.refreshToken) {
      await axios.post(
        buildYowanUrl('auth/logout/'),
        {
          refresh: snapshot.refreshToken,
        },
        {
          headers: buildAuthHeaders(snapshot.accessToken),
        }
      );
    }
  } finally {
    clearYowanStoredSession();
    clearYowanAuthRuntimeState();
  }
};
